import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Input,
  Option,
  Spinner,
  Subtitle1,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  Textarea,
  Title2,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { Add20Regular, ArrowDownload20Regular, Box24Regular, Search20Regular } from '@fluentui/react-icons'
import { useProducts, useCreateProduct } from '../hooks/useProducts'
import { useDemand } from '../hooks/useDemands'
import { useEffectiveUser } from '../hooks/useEffectiveUser'
import { useColumnSort, sortByCriteria } from '../hooks/useColumnSort'
import { useToast } from '../components/ToastProvider'
import { ErrorCard } from '../components/ErrorCard'
import { SortableHeader } from '../components/SortableHeader'
import api from '../lib/api'
import { downloadBlob } from '../lib/download'
import { gateStatusLabel } from '../lib/labels'
import { apiErrorMessage, blobErrorMessage } from '../lib/apiError'
import { FOCUS_AREAS } from '../types/focus_area'
import type { Product, ProductLifecycleStage } from '../types/product'
import { bayerColors } from '../theme'

const BRAND_PRIMARY = bayerColors.navyMid

export const LIFECYCLE_LABELS: Record<ProductLifecycleStage, string> = {
  poc: 'PoC',
  mvp: 'MVP',
  portfolio: 'Portfolio',
  sunset: 'Sunset',
}

const LIFECYCLE_COLORS: Record<ProductLifecycleStage, string> = {
  poc: '#0891b2',
  mvp: '#8b5cf6',
  portfolio: '#16a34a',
  sunset: '#9ca3af',
}

const PRODUCT_STAGE_SORT: Record<string, number> = { poc: 0, mvp: 1, portfolio: 2, sunset: 3 }
type ProductSortKey = 'name' | 'fa' | 'stage' | 'gate' | 'team' | 'source'

function compareProduct(a: Product, b: Product, key: ProductSortKey): number {
  switch (key) {
    case 'name':
      return a.name.localeCompare(b.name)
    case 'fa':
      return a.focus_area_code.localeCompare(b.focus_area_code)
    case 'stage':
      return (PRODUCT_STAGE_SORT[a.lifecycle_stage] ?? 9) - (PRODUCT_STAGE_SORT[b.lifecycle_stage] ?? 9)
    case 'gate':
      return (a.gate_status ?? '').localeCompare(b.gate_status ?? '')
    case 'team':
      return a.team.length - b.team.length
    case 'source':
      return (a.source_demand_id ?? 0) - (b.source_demand_id ?? 0)
    default:
      return 0
  }
}

const STAGE_OPTIONS: { key: '' | ProductLifecycleStage; label: string }[] = [
  { key: '', label: 'All stages' },
  { key: 'poc', label: 'PoC' },
  { key: 'mvp', label: 'MVP' },
  { key: 'portfolio', label: 'Portfolio' },
  { key: 'sunset', label: 'Sunset' },
]

const useStyles = makeStyles({
  root: { maxWidth: '1200px', margin: '0 auto' },
  pageHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: tokens.spacingVerticalL,
    gap: tokens.spacingHorizontalM,
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
    marginBottom: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalM,
    background: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  searchWrap: { flex: '1 1 240px', minWidth: '200px' },
  tableWrap: {
    background: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    overflowX: 'auto',
  },
  row: { cursor: 'pointer', ':hover': { background: '#F5F5F5' } },
  rowTitle: { fontWeight: 600, color: tokens.colorNeutralForeground1 },
  rowMeta: { color: tokens.colorNeutralForeground3, fontSize: '12px', marginTop: '2px' },
  emptyCell: { color: tokens.colorNeutralForeground3 },
  state: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalS,
    padding: '48px 16px',
    color: tokens.colorNeutralForeground3,
    background: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
  },
  formField: { display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px' },
  formLabel: { fontSize: '12px', fontWeight: 600, color: '#424242' },
})

export function ProductList() {
  const styles = useStyles()
  const navigate = useNavigate()
  const effectiveUser = useEffectiveUser()
  const { showToast } = useToast()
  const [searchParams, setSearchParams] = useSearchParams()

  const isAdmin = effectiveUser?.role === 'admin' || effectiveUser?.role === 'super_admin'
  const isCouncil = isAdmin || effectiveUser?.role === 'council'
  const ownFa = effectiveUser?.focus_area_code ?? ''

  const [search, setSearch] = useState('')
  const [faFilter, setFaFilter] = useState('')
  const [stageFilter, setStageFilter] = useState<'' | ProductLifecycleStage>('')
  const [includeArchived, setIncludeArchived] = useState(false)

  const filters = useMemo(
    () => ({
      search: search.trim() || undefined,
      focus_area: faFilter || undefined,
      lifecycle_stage: stageFilter || undefined,
      include_archived: includeArchived,
      page_size: 500,
    }),
    [search, faFilter, stageFilter, includeArchived],
  )
  const { data, isLoading, error, refetch } = useProducts(filters)
  const rows = useMemo(() => data?.items ?? [], [data])
  const total = data?.total ?? 0

  const { criteria, handleSort } = useColumnSort<ProductSortKey>({ key: 'name', dir: 'asc' })
  const sortedRows = useMemo(() => sortByCriteria(rows, criteria, compareProduct), [rows, criteria])
  const [exporting, setExporting] = useState(false)

  async function exportProducts() {
    setExporting(true)
    try {
      const params: Record<string, string> = {}
      if (faFilter) params.focus_area = faFilter
      if (stageFilter) params.lifecycle_stage = stageFilter
      if (search.trim()) params.search = search.trim()
      if (includeArchived) params.include_archived = 'true'
      const res = await api.get('/products/export', { params, responseType: 'blob' })
      downloadBlob('daccelerate-products.xlsx', res.data as Blob)
    } catch (e) {
      showToast({ title: 'Export failed', body: await blobErrorMessage(e), intent: 'error' })
    } finally {
      setExporting(false)
    }
  }

  // ── Create dialog (manual or bridged from a demand via ?from=) ────────────
  const [createOpen, setCreateOpen] = useState(false)
  const [name, setName] = useState('')
  const [fa, setFa] = useState('')
  const [description, setDescription] = useState('')
  const createMutation = useCreateProduct()

  const fromDemandId = searchParams.get('from')
  const { data: fromDemand } = useDemand(fromDemandId ?? undefined)

  // Open the dialog prefilled when arriving via the promote bridge (?from=).
  useEffect(() => {
    if (fromDemandId && fromDemand && !createOpen) {
      setName(fromDemand.title.replace(/^\[TEST\]\s*/, ''))
      setFa(fromDemand.focus_area_code)
      setDescription('')
      setCreateOpen(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDemandId, fromDemand])

  function openManualCreate() {
    // Drop any stale ?from= so a manual create can't silently become a bridge
    // to a demand the user didn't choose.
    if (fromDemandId) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.delete('from')
        return next
      })
    }
    setName('')
    setFa(isAdmin ? '' : ownFa)
    setDescription('')
    setCreateOpen(true)
  }

  function closeCreate() {
    setCreateOpen(false)
    if (fromDemandId) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.delete('from')
        return next
      })
    }
  }

  function submitCreate() {
    if (!name.trim() || !fa) return
    createMutation.mutate(
      {
        name: name.trim(),
        focus_area_code: fa,
        description: description.trim() || undefined,
        // Only bridge when the source demand actually loaded — never coerce a
        // raw/stale ?from= param (which could be NaN or a wrong id).
        source_demand_id: fromDemand ? fromDemand.id : undefined,
      },
      {
        onSuccess: (product) => {
          showToast({ title: `Created ${product.public_id ?? product.name}`, intent: 'success' })
          closeCreate()
          navigate(`/product/${product.id}`)
        },
        onError: (e) =>
          showToast({ title: 'Create failed', body: apiErrorMessage(e), intent: 'error' }),
      },
    )
  }

  const faOptions = isAdmin ? FOCUS_AREAS : FOCUS_AREAS.filter((f) => f.code === ownFa)

  return (
    <section className={styles.root} aria-label="Product list">
      <header className={styles.pageHead}>
        <div>
          <Title2 as="h1" style={{ color: '#242424' }}>
            Products
          </Title2>
          <Caption1 style={{ marginTop: '4px', display: 'block' }}>
            {rows.length} of {total} products
          </Caption1>
        </div>
        {isCouncil && (
          <Button
            appearance="primary"
            icon={<Add20Regular />}
            onClick={openManualCreate}
            data-testid="new-product-button"
            style={{ background: BRAND_PRIMARY, borderColor: BRAND_PRIMARY }}
          >
            New product
          </Button>
        )}
      </header>

      <div className={styles.toolbar} data-testid="product-list-toolbar">
        <div className={styles.searchWrap}>
          <Input
            placeholder="Search products by name or description…"
            value={search}
            onChange={(_, d) => setSearch(d.value)}
            contentBefore={<Search20Regular />}
            aria-label="Search products"
            data-testid="product-search"
          />
        </div>
        <Dropdown
          aria-label="Filter by focus area"
          data-testid="product-fa-filter"
          placeholder="All focus areas"
          value={
            faFilter
              ? `${faFilter} — ${FOCUS_AREAS.find((f) => f.code === faFilter)?.name ?? ''}`
              : 'All focus areas'
          }
          selectedOptions={[faFilter]}
          onOptionSelect={(_, d) => setFaFilter(d.optionValue ?? '')}
        >
          <Option value="">All focus areas</Option>
          {FOCUS_AREAS.map((f) => (
            <Option key={f.code} value={f.code}>{`${f.code} — ${f.name}`}</Option>
          ))}
        </Dropdown>
        <Dropdown
          aria-label="Filter by lifecycle stage"
          data-testid="product-stage-filter"
          placeholder="All stages"
          value={stageFilter ? LIFECYCLE_LABELS[stageFilter] : 'All stages'}
          selectedOptions={[stageFilter]}
          onOptionSelect={(_, d) => setStageFilter((d.optionValue ?? '') as '' | ProductLifecycleStage)}
        >
          {STAGE_OPTIONS.map((s) => (
            <Option key={s.key || 'all'} value={s.key}>
              {s.label}
            </Option>
          ))}
        </Dropdown>
        <Button
          appearance={includeArchived ? 'primary' : 'subtle'}
          size="small"
          onClick={() => setIncludeArchived((v) => !v)}
          data-testid="product-toggle-archived"
        >
          {includeArchived ? 'Hide archived' : 'Show archived'}
        </Button>
        <Button
          appearance="subtle"
          size="small"
          icon={<ArrowDownload20Regular />}
          onClick={exportProducts}
          disabled={exporting || sortedRows.length === 0}
          data-testid="product-export"
          title="Export the current view to Excel (.xlsx)"
        >
          {exporting ? 'Exporting…' : 'Export'}
        </Button>
      </div>

      {isLoading ? (
        <div className={styles.state} data-testid="product-list-loading">
          <Spinner size="tiny" />
          <Body1>Loading products…</Body1>
        </div>
      ) : error ? (
        <div data-testid="product-list-error">
          <ErrorCard
            title="Couldn't load products"
            message={apiErrorMessage(error, 'Please try again.')}
            retry={() => {
              void refetch()
            }}
          />
        </div>
      ) : rows.length === 0 ? (
        <div className={styles.state} data-testid="product-list-empty">
          <Box24Regular />
          <Subtitle1>No products yet</Subtitle1>
          <Caption1>
            Products are created when a demand is promoted, or added manually with “New product”.
          </Caption1>
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <Table
            aria-label="Product list table"
            data-testid="product-list-table"
            style={{ minWidth: 760 }}
          >
            <TableHeader>
              <TableRow>
                <SortableHeader sortKey="name" criteria={criteria} onSort={handleSort} width="46%">
                  Name
                </SortableHeader>
                <SortableHeader sortKey="fa" criteria={criteria} onSort={handleSort} width="10%">
                  FA
                </SortableHeader>
                <SortableHeader sortKey="stage" criteria={criteria} onSort={handleSort} width="14%">
                  Stage
                </SortableHeader>
                <SortableHeader sortKey="gate" criteria={criteria} onSort={handleSort} width="14%">
                  Gate
                </SortableHeader>
                <SortableHeader sortKey="team" criteria={criteria} onSort={handleSort} width="8%">
                  Team
                </SortableHeader>
                <SortableHeader sortKey="source" criteria={criteria} onSort={handleSort} width="8%">
                  From
                </SortableHeader>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedRows.map((p) => (
                <TableRow
                  key={p.id}
                  className={styles.row}
                  data-testid={`product-row-${p.id}`}
                  onClick={() => navigate(`/product/${p.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      navigate(`/product/${p.id}`)
                    }
                  }}
                  tabIndex={0}
                >
                  <TableCell>
                    <div
                      className={styles.rowTitle}
                      style={p.archived_at ? { textDecoration: 'line-through', color: '#9e9e9e' } : undefined}
                    >
                      {p.name}
                    </div>
                    <div className={styles.rowMeta}>
                      {p.public_id ?? `#${p.id}`} · {p.origin}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge appearance="tint" color="brand">
                      {p.focus_area_code}
                    </Badge>
                  </TableCell>
                  <TableCell data-testid={`product-row-stage-${p.id}`}>
                    <Badge
                      appearance="tint"
                      style={{
                        background: `${LIFECYCLE_COLORS[p.lifecycle_stage]}1a`,
                        color: LIFECYCLE_COLORS[p.lifecycle_stage],
                      }}
                    >
                      {LIFECYCLE_LABELS[p.lifecycle_stage]}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {p.gate_status ? (
                      <Badge appearance="tint" color={p.gate_status === 'overdue' ? 'danger' : 'warning'}>
                        {gateStatusLabel(p.gate_status)}
                      </Badge>
                    ) : (
                      <Caption1 className={styles.emptyCell}>—</Caption1>
                    )}
                  </TableCell>
                  <TableCell>
                    {p.team.length > 0 ? (
                      <Badge appearance="tint" color="informative" size="small">
                        {p.team.length}
                      </Badge>
                    ) : (
                      <Caption1 className={styles.emptyCell}>—</Caption1>
                    )}
                  </TableCell>
                  <TableCell>
                    {p.source_demand_id ? (
                      <Caption1>#{p.source_demand_id}</Caption1>
                    ) : (
                      <Caption1 className={styles.emptyCell}>—</Caption1>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={(_, d) => (!d.open ? closeCreate() : undefined)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{fromDemandId ? 'Create product from demand' : 'New product'}</DialogTitle>
            <DialogContent>
              {fromDemandId && fromDemand && (
                <Caption1 style={{ display: 'block', marginBottom: '12px' }}>
                  Graduating <strong>{fromDemand.demand_id ?? `#${fromDemand.id}`}</strong> —{' '}
                  {fromDemand.title}
                </Caption1>
              )}
              <div className={styles.formField}>
                <label className={styles.formLabel} htmlFor="product-name-input">
                  Name
                </label>
                <Input
                  id="product-name-input"
                  value={name}
                  onChange={(_, d) => setName(d.value)}
                  data-testid="product-name-input"
                />
              </div>
              <div className={styles.formField}>
                <label className={styles.formLabel} htmlFor="product-fa-input">
                  Focus area
                </label>
                <Dropdown
                  id="product-fa-input"
                  data-testid="product-fa-input"
                  placeholder="Select a focus area"
                  disabled={!!fromDemandId}
                  value={fa ? `${fa} — ${FOCUS_AREAS.find((f) => f.code === fa)?.name ?? ''}` : ''}
                  selectedOptions={[fa]}
                  onOptionSelect={(_, d) => setFa(d.optionValue ?? '')}
                >
                  {faOptions.map((f) => (
                    <Option key={f.code} value={f.code}>{`${f.code} — ${f.name}`}</Option>
                  ))}
                </Dropdown>
              </div>
              <div className={styles.formField}>
                <label className={styles.formLabel} htmlFor="product-desc-input">
                  Description (optional)
                </label>
                <Textarea
                  id="product-desc-input"
                  value={description}
                  onChange={(_, d) => setDescription(d.value)}
                  rows={3}
                  data-testid="product-desc-input"
                />
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={closeCreate}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                disabled={!name.trim() || !fa || createMutation.isPending}
                onClick={submitCreate}
                data-testid="product-create-confirm"
              >
                {createMutation.isPending ? 'Creating…' : 'Create product'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </section>
  )
}

export default ProductList
